export function parseColorMixToHex(color: string): string {
  const hexRegex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

  // Si ya es hexadecimal, retornar tal cual
  if (hexRegex.test(color.trim())) {
    return color;
  }

  const mixRegex =
    /^color-mix\(in\s+srgb\s*,\s*(#[0-9a-f]{3,6}|[a-z]+)\s*,\s*(#[0-9a-f]{3,6}|[a-z]+)\s+(\d{1,3})%\)$/i;
  const match = color.match(mixRegex);

  if (!match) {
    throw new Error("Formato de color no soportado");
  }

  const [, color1, color2, percentStr] = match;
  const percent = parseInt(percentStr, 10);
  const p2 = percent / 100;
  const p1 = 1 - p2;

  // Función auxiliar para convertir nombre de color a RGB si es necesario
  function colorToRGB(col: string): [number, number, number] {
    const ctx = document.createElement("canvas").getContext("2d")!;
    ctx.fillStyle = col;
    const computed = ctx.fillStyle;

    // Convertir a formato hexadecimal si fue interpretado
    const hex = computed.startsWith("#") ? computed : "#000000";

    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
  }

  const [r1, g1, b1] = colorToRGB(color1);
  const [r2, g2, b2] = colorToRGB(color2);

  const r = Math.round(r1 * p1 + r2 * p2);
  const g = Math.round(g1 * p1 + g2 * p2);
  const b = Math.round(b1 * p1 + b2 * p2);

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

export function generateShades(color: string): string[] {
  const shades = [
    adjustColor(color, 0.9), // 50
    adjustColor(color, 0.7), // 100
    adjustColor(color, 0.5), // 200
    adjustColor(color, 0.3), // 300
    adjustColor(color, 0.1), // 400
    color, // 500 (color base)
    adjustColor(color, -0.1), // 600
    adjustColor(color, -0.3), // 700
    adjustColor(color, -0.5), // 800
    adjustColor(color, -0.7), // 900
    adjustColor(color, -0.9), // 950
  ];

  return shades;
}

export function adjustColor(color: string, percent: number): string {
  const f = parseInt(color.slice(1), 16);
  const t = percent < 0 ? 0 : 255;
  const p = percent < 0 ? percent * -1 : percent;
  const R = (f >> 16) + (t - (f >> 16)) * p;
  const G = ((f >> 8) & 0x00ff) + (t - ((f >> 8) & 0x00ff)) * p;
  const B = (f & 0x0000ff) + (t - (f & 0x0000ff)) * p;
  return `#${(
    0x1000000 +
    (Math.round(R) << 16) +
    (Math.round(G) << 8) +
    Math.round(B)
  )
    .toString(16)
    .slice(1)
    .toUpperCase()}`;
}

export function getContrastColor(hexColor: string): string {
  // Elimina el símbolo '#' si está presente
  hexColor = hexColor.replace("#", "");

  // Convierte el color hexadecimal a valores RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Calcula la luminancia
  // Luminancia=0.299×R+0.587×G+0.114×B
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

  // Determina el color de contraste
  return luminance > 128 ? "color-mix(in srgb, #161616,white 10%)" : "#FFFFFF";
}

export function hexToRgba(hex: string, alpha: number): string {
  // Elimina el símbolo '#' si existe
  hex = hex.replace(/^#/, "");

  // Si es formato corto (#abc), lo expandimos a #aabbcc
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function generateGradient(hex: string): string {
  const rgba0 = hexToRgba(hex, 0.5);
  const rgba20 = hexToRgba(hex, 0.2);
  const rgba100 = hexToRgba(hex, 1);

  return `linear-gradient(180deg, ${rgba0} 0%, ${rgba20} 20%, ${rgba100} 100%)`;
}
